/**
 * Utility functions for serializing/deserializing filter objects to/from URL parameters
 */

/**
 * Serializes a filter object to a base64-encoded URL-safe string
 * @param filters - The filter object to serialize
 * @returns Base64-encoded string or null if filters are empty
 */
export const serializeFiltersToUrl = (filters: Record<string, any> | null | undefined): string | null => {
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  try {
    // Remove empty values and null/undefined
    const cleanedFilters = cleanFilterObject(filters);

    if (Object.keys(cleanedFilters).length === 0) {
      return null;
    }

    // Convert to JSON string
    const jsonString = JSON.stringify(cleanedFilters);

    // Encode to base64 (URL-safe)
    const base64 = btoa(jsonString);

    // Make it URL-safe by replacing characters
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error serializing filters:', error);
    return null;
  }
};

/**
 * Deserializes a base64-encoded string back to a filter object
 * @param encodedFilters - The base64-encoded filter string from URL
 * @returns The filter object or null if invalid
 */
export const deserializeFiltersFromUrl = (encodedFilters: string | null): Record<string, any> | null => {
  if (!encodedFilters) {
    return null;
  }

  try {
    // Reverse URL-safe encoding
    let base64 = encodedFilters.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    // Decode from base64
    const jsonString = atob(base64);

    // Parse JSON
    const filters = JSON.parse(jsonString);

    return filters;
  } catch (error) {
    console.error('Error deserializing filters:', error);
    return null;
  }
};

/**
 * Recursively removes empty values, null, undefined, and empty objects/arrays from filter object
 * @param obj - The object to clean
 * @returns Cleaned object
 */
const cleanFilterObject = (obj: any): any => {
  if (Array.isArray(obj)) {
    const cleaned = obj.filter((item) => item !== null && item !== undefined && item !== '');
    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (obj !== null && typeof obj === 'object') {
    const cleaned: Record<string, any> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          const cleanedArray = cleanFilterObject(value);
          if (cleanedArray && cleanedArray.length > 0) {
            cleaned[key] = cleanedArray;
          }
        } else if (typeof value === 'object') {
          const cleanedObj = cleanFilterObject(value);
          if (cleanedObj && Object.keys(cleanedObj).length > 0) {
            cleaned[key] = cleanedObj;
          }
        } else {
          cleaned[key] = value;
        }
      }
    }

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  return obj;
};

/**
 * Merges table filters (from URL) with other filters
 * @param baseFilters - Base filters object
 * @param tableFilters - Table-specific filters from URL (the deserialized filters object)
 * @returns Merged filter object
 */
export const mergeTableFilters = (baseFilters: Record<string, any>, tableFilters: Record<string, any> | null): Record<string, any> => {
  if (!tableFilters) {
    return baseFilters;
  }

  // The tableFilters from URL is the entire nested structure
  // Merge it with base filters
  const merged = { ...baseFilters };

  // Merge the filters property (which contains all the nested filter groups)
  merged.filters = {
    ...baseFilters.filters,
    ...tableFilters,
  };

  return merged;
};
